// ✅ src/pages/VehicleV2/DayWiseLubeOutwardReport.jsx
// UPDATED — Production listing template
// 3-column filters • page overflow locked • day columns scroll in table

import React, { useEffect, useMemo, useState, useContext } from "react";
import axios from "axios";
import {
  FaSearch,
  FaPrint,
  FaArrowLeft,
  FaFileExport,
  FaEye,
  FaSyncAlt,
  FaChevronDown,
  FaChevronUp,
} from "react-icons/fa";
import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";

const API_BASE = "https://nissi-india.com/api/daywiseLubeOutwardReport";

const toStr = (v) => String(v ?? "").trim();

const buildFinYearHeaders = () => {
  const finYearId =
    localStorage.getItem("finYearId") ||
    localStorage.getItem("x-finyearid") ||
    localStorage.getItem("FinYearId") ||
    "";
  const finYear =
    localStorage.getItem("finYear") ||
    localStorage.getItem("x-finyear") ||
    localStorage.getItem("FinYear") ||
    "";
  const branchType =
    localStorage.getItem("branchType") ||
    localStorage.getItem("x-branchtype") ||
    "";
  const branchId =
    localStorage.getItem("branchId") ||
    localStorage.getItem("x-branchid") ||
    "";
  const h = {};
  if (finYearId) h["x-finyearid"] = finYearId;
  if (finYear) h["x-finyear"] = finYear;
  if (branchType) h["x-branchtype"] = branchType;
  if (branchId) h["x-branchid"] = branchId;
  return h;
};

const withSelect = (arr) => [{ label: "Select", value: "" }, ...(arr || [])];

const ID_COL_RE = /(^id$|_id$|typeid|categoryid|branchid)/i;
const IMPORTANT_COL_RE = /name|reg|code|vehicle|item|total|qty|branch|store/i;
const DAY_COL_RE = /^(d)?([1-9]|[12][0-9]|3[01])$/i;

const DayWiseLubeOutwardReport = () => {
  const navigate = useNavigate();
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;
  const tableHeaderClass = buttonClass;

  const [showFilters, setShowFilters] = useState(true);
  const [loadingInit, setLoadingInit] = useState(false);
  const [loadingList, setLoadingList] = useState(false);
  const isBusy = loadingInit || loadingList;

  const [branches, setBranches] = useState([]);
  const [months, setMonths] = useState([]);
  const [years, setYears] = useState([]);
  const [itemCategories, setItemCategories] = useState([]);
  const [itemNames, setItemNames] = useState([]);
  const [highlightItems, setHighlightItems] = useState(new Set());
  const [userLock, setUserLock] = useState({ branchLocked: false, branchValue: "" });

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [rows, setRows] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [columns, setColumns] = useState([]);
  const [totalDays, setTotalDays] = useState(0);

  const [filters, setFilters] = useState({
    branch: "",
    month: "",
    year: "",
    itemCategory: "",
    itemName: "",
  });

  const monthText = useMemo(() => {
    const m = months.find((x) => String(x.value) === String(filters.month));
    return m?.text || m?.label || "";
  }, [months, filters.month]);

  const selectedItemLabel = useMemo(() => {
    const it = itemNames.find((x) => String(x.value) === String(filters.itemName));
    return it?.label || "";
  }, [itemNames, filters.itemName]);

  const displayColumns = useMemo(() => {
    const all = columns?.length ? columns : rows[0] ? Object.keys(rows[0]) : [];
    if (!all.length) return [];
    const kept = all.filter((c) => IMPORTANT_COL_RE.test(c) || DAY_COL_RE.test(String(c)) || /total/i.test(c));
    const cleaned = kept.filter((c) => !ID_COL_RE.test(String(c)));
    return cleaned.length ? cleaned : all;
  }, [columns, rows]);

  const fieldClass = `w-full max-w-full min-w-0 box-border px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
    isDark
      ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
      : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
  }`;

  const labelClass = `block mb-2 text-sm font-medium ${isDark ? "text-gray-300" : "text-gray-700"}`;

  useEffect(() => {
    const run = async () => {
      setLoadingInit(true);
      try {
        const headers = buildFinYearHeaders();
        const branchType = headers["x-branchtype"] || "";
        const branchId = headers["x-branchid"] || "";
        const rs = await axios.get(`${API_BASE}/init`, {
          headers,
          params: { branchType, branchId },
        });
        if (!rs.data?.success) throw new Error(rs.data?.message || "Init failed");

        setBranches(withSelect(rs.data.branches));
        setMonths(withSelect(rs.data.months));
        setYears(withSelect((rs.data.years || []).map((y) => ({ label: y, value: y }))));
        setItemCategories(withSelect(rs.data.itemCategories));
        setItemNames(withSelect(rs.data.itemNames));

        const fixedCat = rs.data?.fixed?.itemCategoryId || "37";
        setFilters((p) => ({ ...p, itemCategory: fixedCat }));

        const lock = rs.data?.userLock || { branchLocked: false, branchValue: "" };
        setUserLock(lock);
        if (lock?.branchLocked && lock?.branchValue) {
          setFilters((p) => ({ ...p, branch: String(lock.branchValue) }));
        }
      } catch (e) {
        console.error(e);
        toast.error(e?.message || "Failed to load init data");
      } finally {
        setLoadingInit(false);
      }
    };
    run();
  }, []);

  useEffect(() => {
    const canFetch = !!filters.branch && !!monthText && !!filters.year && !loadingInit;
    if (!canFetch) {
      setHighlightItems(new Set());
      return;
    }
    const run = async () => {
      try {
        const headers = buildFinYearHeaders();
        const rs = await axios.get(`${API_BASE}/highlights`, {
          headers,
          params: { branchId: filters.branch, monthText, year: filters.year },
        });
        if (!rs.data?.success) return;
        const list = rs.data?.itemNamesToHighlight || [];
        setHighlightItems(new Set(list.map((x) => String(x).trim().toLowerCase())));
      } catch {
        setHighlightItems(new Set());
      }
    };
    run();
  }, [filters.branch, monthText, filters.year, loadingInit]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFilters((p) => ({ ...p, [name]: value }));
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    setFilters((p) => ({
      ...p,
      branch: userLock?.branchLocked ? String(userLock.branchValue || "") : "",
      month: "",
      year: "",
      itemName: "",
      itemCategory: p.itemCategory || "37",
    }));
    setRows([]);
    setTotalRows(0);
    setColumns([]);
    setTotalDays(0);
    setCurrentPage(1);
    setHighlightItems(new Set());
    toast.info("Filters cleared!");
  };

  const fetchList = async (page = 1, ignoreItem = false) => {
    if (!filters.month) return toast.warning("Please Select the Month to search");
    if (!filters.year) return toast.warning("Please Select Year to search");
    if (!filters.branch) return toast.warning("Please Select Branch to search");

    setLoadingList(true);
    try {
      const headers = buildFinYearHeaders();
      const rs = await axios.get(`${API_BASE}/list`, {
        headers,
        params: {
          branchId: filters.branch,
          monthValue: filters.month,
          monthText,
          year: filters.year,
          itemCategoryId: filters.itemCategory || "37",
          itemId: ignoreItem ? "Select" : filters.itemName || "Select",
          page,
          pageSize: itemsPerPage,
        },
      });
      if (!rs.data?.success) throw new Error(rs.data?.message || "Search failed");
      setRows(rs.data.rows || []);
      setTotalRows(rs.data.total || 0);
      setColumns(rs.data.columns || []);
      setTotalDays(rs.data.totaldays || 0);
      setCurrentPage(page);
      toast.success(`Found ${rs.data.total || 0} record(s)`);
    } catch (e) {
      console.error(e);
      toast.error(e?.message || "Failed to fetch report");
    } finally {
      setLoadingList(false);
    }
  };

  const handleSearch = async () => fetchList(1, false);

  const handleViewAll = async () => {
    if (!filters.month || !filters.year) {
      toast.info("Please select Month and Year to view records.");
      return;
    }
    await fetchList(1, true);
  };

  const handlePrint = () => {
    if (!filters.month) return toast.warning("Please Select the Month to print");
    if (!filters.year) return toast.warning("Please Select Year to print");
    if (!filters.branch) return toast.warning("Please Select Branch to print");
    navigate("/vehicle/DayWiseLubeOutwardReport_Print", {
      state: {
        branchId: filters.branch,
        monthValue: filters.month,
        monthText,
        year: filters.year,
        itemCategoryId: filters.itemCategory || "37",
        itemId: filters.itemName || "Select",
        itemNameText: selectedItemLabel,
        totalDays,
        totalRows,
      },
    });
  };

  const handleExport = () => {
    toast.info("Export not wired yet (add /exportCsv backend if needed).");
  };

  const handleRefresh = async () => {
    if (filters.month && filters.year && filters.branch) {
      await fetchList(currentPage, false);
    } else {
      toast.info("Select Branch, Month and Year then Search.");
    }
  };

  const totalPages = Math.max(1, Math.ceil(totalRows / itemsPerPage));
  const paginate = (pageNumber) => {
    const p = Math.max(1, Math.min(totalPages, pageNumber));
    fetchList(p, false);
  };

  const optionStyle = (label) => {
    const key = String(label || "").trim().toLowerCase();
    if (!key) return undefined;
    if (highlightItems.has(key)) return { backgroundColor: "#00e6e6" };
    return undefined;
  };

  const statusText = loadingInit ? "Loading dropdowns..." : loadingList ? "Searching..." : "Ready";

  return (
    <VehicleLayoutWrapper>
      <div className={`w-full max-w-full min-w-0 overflow-x-hidden box-border min-h-screen p-4 md:p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-2xl md:text-3xl">DAY WISE LUBE OUTWARD REPORT</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div className={`w-full max-w-full min-w-0 overflow-hidden p-4 md:p-6 mt-6 rounded-2xl shadow-xl border box-border ${
          isDark
            ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
            : "bg-white text-black"
        }`}>
          <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
            <div className="min-w-0">
              <h2 className={`text-xl md:text-2xl font-bold break-words ${isDark ? "text-white" : "text-gray-900"}`}>
                Day Wise Lube Outward Report
              </h2>
            </div>
            <div className="flex items-center gap-2 md:gap-3 flex-wrap">
              <button onClick={handlePrint} type="button" className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-200 ${buttonClass} ${hoverClass} text-white`}>
                <FaPrint /> Print
              </button>
              <button onClick={handleExport} type="button" className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform transition duration-200 ${isDark ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600" : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"}`}>
                <FaFileExport /> Export
              </button>
              <button onClick={handleRefresh} disabled={isBusy} type="button" className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform transition duration-200 ${isDark ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600" : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"} ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}>
                <FaSyncAlt /> Refresh
              </button>
              <button onClick={() => navigate(-1)} type="button" className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform transition duration-200 ${isDark ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600" : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"}`}>
                <FaArrowLeft /> Back
              </button>
              <button onClick={() => setShowFilters((s) => !s)} type="button" className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform transition duration-200 ${isDark ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600" : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"}`}>
                {showFilters ? <FaChevronUp /> : <FaChevronDown />}
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>
            </div>
          </div>

          {showFilters && (
            <div className={`p-4 md:p-6 rounded-lg mb-6 overflow-hidden ${isDark ? "bg-gray-800/50" : "bg-blue-50"}`}>
              <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                <h3 className={`text-lg font-semibold ${isDark ? "text-white" : "text-blue-900"}`}>Search Criteria</h3>
                <div className="flex gap-2 flex-wrap">
                  <button onClick={handleClearFilters} type="button" disabled={isBusy} className={`px-4 py-2 rounded-lg text-sm font-semibold border ${isDark ? "border-gray-600 text-gray-200 hover:bg-gray-700" : "border-gray-300 text-gray-700 hover:bg-white"} ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}>Clear</button>
                  <button onClick={handleViewAll} disabled={isBusy} type="button" className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold border ${isDark ? "border-gray-600 text-gray-200 hover:bg-gray-700" : "border-gray-300 text-gray-700 hover:bg-white"} ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}><FaEye /> View All</button>
                  <button onClick={handleSearch} disabled={isBusy} type="button" className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${isBusy ? "opacity-60 cursor-not-allowed" : ""} ${buttonClass} ${hoverClass} text-white`}><FaSearch /> {loadingList ? "Searching..." : "Search"}</button>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                <div className="min-w-0">
                  <label className={labelClass}>Branch</label>
                  <select name="branch" value={filters.branch} onChange={handleChange} className={fieldClass} disabled={!!userLock?.branchLocked || isBusy}>
                    {branches.map((b) => (
                      <option key={b.value || b.label} value={b.value}>{b.label}</option>
                    ))}
                  </select>
                </div>
                <div className="min-w-0">
                  <label className={labelClass}>Month</label>
                  <select name="month" value={filters.month} onChange={handleChange} className={fieldClass} disabled={isBusy}>
                    {months.map((m) => (
                      <option key={m.value || m.label} value={m.value}>{m.label}</option>
                    ))}
                  </select>
                </div>
                <div className="min-w-0">
                  <label className={labelClass}>Year</label>
                  <select name="year" value={filters.year} onChange={handleChange} className={fieldClass} disabled={isBusy}>
                    {years.map((y) => (
                      <option key={y.value || y.label} value={y.value}>{y.label}</option>
                    ))}
                  </select>
                </div>
                <div className="min-w-0">
                  <label className={labelClass}>Item Category</label>
                  <select name="itemCategory" value={filters.itemCategory} onChange={handleChange} className={fieldClass} disabled>
                    {itemCategories.map((c) => (
                      <option key={c.value || c.label} value={c.value}>{c.label}</option>
                    ))}
                  </select>
                </div>
                <div className="min-w-0">
                  <label className={labelClass}>Item Name</label>
                  <select name="itemName" value={filters.itemName} onChange={handleChange} className={fieldClass} disabled={isBusy}>
                    {itemNames.map((it) => (
                      <option key={it.value || it.label} value={it.value} style={optionStyle(it.label)}>{it.label}</option>
                    ))}
                  </select>
                </div>
                <div className="min-w-0 flex items-end">
                  <div className={`text-sm pb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    {totalDays ? <>Total Days: <b>{totalDays}</b></> : "Select month/year to load days"}
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <div className={`px-3 py-1 rounded-full text-sm font-medium ${isDark ? "bg-blue-900 text-blue-100" : "bg-blue-100 text-blue-800"}`}>
              Total Records: {totalRows}
            </div>
            <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>{statusText}</div>
          </div>

          <div className="w-full max-w-full overflow-x-auto rounded-lg border shadow-lg">
            {loadingInit || loadingList ? (
              <div className={`p-16 text-center ${isDark ? "bg-gray-800" : "bg-gray-50"}`}>
                <div className={`text-lg font-medium ${isDark ? "text-gray-300" : "text-gray-600"}`}>Loading...</div>
              </div>
            ) : rows.length === 0 ? (
              <div className={`p-16 text-center ${isDark ? "bg-gray-800" : "bg-gray-50"}`}>
                <div className={`text-lg font-medium ${isDark ? "text-gray-400" : "text-gray-500"}`}>No Records Found</div>
                <p className={`mt-2 text-sm ${isDark ? "text-gray-500" : "text-gray-400"}`}>Select Month / Year / Branch and click Search.</p>
              </div>
            ) : (
              <table className={`w-full ${isDark ? "bg-gray-800" : "bg-white"}`}>
                <thead className={`${tableHeaderClass} text-white`}>
                  <tr>
                    {displayColumns.map((col) => (
                      <th key={col} className="px-3 py-3 text-left text-sm font-semibold whitespace-nowrap">{col}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className={`${isDark ? "divide-gray-700" : "divide-gray-200"} divide-y`}>
                  {rows.map((r, idx) => (
                    <tr key={idx} className={`${idx % 2 === 0 ? (isDark ? "bg-gray-800" : "bg-white") : (isDark ? "bg-gray-750" : "bg-gray-50")} ${isDark ? "hover:bg-gray-700" : "hover:bg-blue-50"}`}>
                      {displayColumns.map((col) => (
                        <td key={col} className={`px-3 py-3 text-sm whitespace-nowrap ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r?.[col] ?? ""}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {totalRows > 0 && (
            <div className="mt-6 flex flex-col sm:flex-row justify-between items-center gap-3">
              <div className={`text-sm ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                Showing {(currentPage - 1) * itemsPerPage + 1} to {Math.min(currentPage * itemsPerPage, totalRows)} of {totalRows} entries
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <button onClick={() => paginate(currentPage - 1)} disabled={currentPage === 1 || loadingList} type="button" className={`px-4 py-2 border rounded-lg font-semibold ${isDark ? "border-gray-600 text-gray-300 hover:bg-gray-700" : "border-gray-300 text-gray-700 hover:bg-gray-100"} ${currentPage === 1 || loadingList ? "opacity-50 cursor-not-allowed" : ""}`}>Prev</button>
                <div className={`px-4 py-2 border rounded-lg ${isDark ? "border-gray-600 text-gray-300" : "border-gray-300 text-gray-700"}`}>Page {currentPage} / {totalPages}</div>
                <button onClick={() => paginate(currentPage + 1)} disabled={currentPage === totalPages || loadingList} type="button" className={`px-4 py-2 border rounded-lg font-semibold ${isDark ? "border-gray-600 text-gray-300 hover:bg-gray-700" : "border-gray-300 text-gray-700 hover:bg-gray-100"} ${currentPage === totalPages || loadingList ? "opacity-50 cursor-not-allowed" : ""}`}>Next</button>
              </div>
            </div>
          )}

          <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} closeOnClick pauseOnHover theme={isDark ? "dark" : "light"} />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
};

export default DayWiseLubeOutwardReport;